import React, { useCallback, useState } from 'react';
import { useParams } from 'react-router';
import { useDispatch } from '@tmapy/redux';

import {
  changeDeleteSuccessAlertVisibility,
  changeSubmitSuccessAlertVisibility,
} from '../../app/actions';
import { QUERY_STATE } from '../../constants';
import { useHistoryAction, useSelector } from '../../hooks';
import { actionResourceInvalidate } from '../../redux/resource/actions';

import { priceListItemsCreate } from '../../openapi/operations/priceListItemsCreate';
import { priceListItemsDestroy } from '../../openapi/operations/priceListItemsDestroy';
import { priceListItemsListOperation } from '../../openapi/operations/priceListItemsList';
import { usePriceListItemsRetrieveResource } from '../../openapi/operations/priceListItemsRetrieve';
import { priceListItemsUpdate } from '../../openapi/operations/priceListItemsUpdate';
import type { PriceListItemRequest } from '../../openapi/types/PriceListItem';
import { stripEmptyFilters } from '../../utils/queryArgs';

import { PRICE_LIST_ITEM_FORM_TYPE, PRICE_LIST_ITEM_IN_USE_CODE } from '../constants';
import { PriceListItemForm } from './PriceListItemForm';

export const PriceListItem: React.FC = () => {
  const dispatch = useDispatch();
  const { id } = useParams<{ id?: string }>();
  const priceListItemId = id ? parseInt(id, 10) : null;
  const formType = priceListItemId ? PRICE_LIST_ITEM_FORM_TYPE.EDIT : PRICE_LIST_ITEM_FORM_TYPE.NEW;
  const resource = usePriceListItemsRetrieveResource(
    priceListItemId ? { id: priceListItemId } : null,
  );

  const routeToPriceList = useHistoryAction('vydejPriceList');
  const filters = useSelector((state) => state.filter.priceListItemList);

  const [submitQueryState, setSubmitQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [deleteQueryState, setDeleteQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [isDeleteBlockedByUse, setDeleteBlockedByUse] = useState(false);

  const invalidateList = useCallback(() => {
    // Přehled cachuje pod cestou včetně filtru, takže samotná nefiltrovaná
    // cesta nestačí - po návratu tlačítkem Zpět (POP => useCached) by se
    // jinak zobrazil neaktuální seznam.
    const paths = new Set([
      priceListItemsListOperation({}).path,
      priceListItemsListOperation(stripEmptyFilters(filters)).path,
    ]);
    paths.forEach((path) => dispatch(actionResourceInvalidate(path)));
  }, [dispatch, filters]);

  const onSubmit = useCallback(
    async (values: PriceListItemRequest) => {
      try {
        setSubmitQueryState(QUERY_STATE.WAITING);
        if (priceListItemId) {
          const response = await priceListItemsUpdate({
            query: { id: priceListItemId },
            request: values,
          });
          if (response.ok) {
            setSubmitQueryState(QUERY_STATE.SUCCESS);
            resource.refetch();
          } else {
            setSubmitQueryState(QUERY_STATE.FAIL);
          }
        } else {
          const response = await priceListItemsCreate({ request: values });
          if (response.ok) {
            setSubmitQueryState(QUERY_STATE.SUCCESS);
            dispatch(changeSubmitSuccessAlertVisibility(true));
            routeToPriceList();
          } else {
            setSubmitQueryState(QUERY_STATE.FAIL);
          }
        }
        invalidateList();
      } catch (e) {
        setSubmitQueryState(QUERY_STATE.FAIL);
      }
    },
    [priceListItemId, dispatch, routeToPriceList, invalidateList, resource],
  );

  const onDelete = useCallback(async () => {
    try {
      setDeleteQueryState(QUERY_STATE.WAITING);
      setDeleteBlockedByUse(false);
      const response = await priceListItemsDestroy({ query: { id: priceListItemId! } });
      if (response.ok) {
        dispatch(changeDeleteSuccessAlertVisibility(true));
        invalidateList();
        routeToPriceList();
        setDeleteQueryState(QUERY_STATE.SUCCESS);
      } else {
        // 400 s tímto kódem znamená navázanou výdejní sadu, ne selhání requestu.
        const serverErrors = response.error?.data;
        setDeleteBlockedByUse(
          Array.isArray(serverErrors) &&
            serverErrors.some((error) => error?.code === PRICE_LIST_ITEM_IN_USE_CODE),
        );
        setDeleteQueryState(QUERY_STATE.FAIL);
      }
    } catch (e) {
      setDeleteQueryState(QUERY_STATE.FAIL);
    }
  }, [priceListItemId, dispatch, routeToPriceList, invalidateList]);

  return (
    <PriceListItemForm
      key={`${resource.data?.id}-${resource.fetchedAt}`}
      formType={formType}
      resource={resource}
      submitQueryState={submitQueryState}
      deleteQueryState={deleteQueryState}
      isDeleteBlockedByUse={isDeleteBlockedByUse}
      onSubmit={onSubmit}
      onDelete={onDelete}
    />
  );
};
